import React, { useCallback, useMemo } from 'react';
import { TaskScore } from './TaskScore.component';
import { ActiveTaskScore } from './ActiveTaskScore.component';
import { ExamTask } from '../../../models/assessment.types';
import { ExamTaskGroupSolutionsNavigation } from '../../../pages/Assessment/Assessment.types';
import { useStyles } from './AssessmentList.styles';
import { ExaminerFunction } from '../../../pages/Assessment/Assessment.types';
import { AssessmentSessionProcessTypeId, Status } from '../../../services/exams/assessmentSession/assessments.types';
import { EmptyTasksList } from './EmptyTasksList.component';
import { SolutionViewMode } from '../../../pages/Solution/Solution.component';
import { ToleranceRangeTask } from '../../../pages/Sessions/SessionsParametersDialog/ToleranceRange/ToleranceRange.types';
import { useScoring } from '../../../Context/Scoring.context';

interface AssessmentListProps {
  mode: SolutionViewMode;
  activeTaskIndex: number;
  activeCriterionIndex: number;
  activeTaskNo?: string;
  onTaskChange: (value: 'next' | 'previous') => void;
  viewType: 'cards' | 'solutions';
  examTaskGroupSolutionsNavigation: ExamTaskGroupSolutionsNavigation;
  onCriterionChange: (value: 'next' | 'previous') => void;
  examTasks: ExamTask[];
  onOpenTask: (taskNumber: string) => void;
  onOpenCriterion: (taskNumber: string, examScoringSchemaId: string) => void;
  examinerFunction: ExaminerFunction;
  examTaskGroupSolutionScoreState: Status;
  examinerId: string;
  canEditScore: boolean;
  processTypeId: AssessmentSessionProcessTypeId;
  tasksToleranceRanges: ToleranceRangeTask[];
  orgzId: string;
  anyAnnotationForGradingProvided: boolean;
  isFullscreen: boolean;
  isAnyModalOpened: boolean;
  keyboardShortcutsDisabled: boolean;
  isSkipLink: boolean;
}

export const AssessmentList: React.FC<AssessmentListProps> = (props) => {
  const classes = useStyles();
  const { activeTask, activeCriterion } = useScoring();

  const handleSelectTask = useCallback(
    (taskNumber: string) => {
      props.onOpenTask(taskNumber);
    },
    [props.onOpenTask],
  );

  const handleSelectCriterion = useCallback(
    (taskNumber: string, examScoringSchemaId: string) => {
      props.onOpenCriterion(taskNumber, examScoringSchemaId);
    },
    [props.onOpenCriterion],
  );

  const taskToleranceRange = props.tasksToleranceRanges.find(
    (taskData: ToleranceRangeTask) => taskData.taskId === activeTask?.taskId,
  );

  const getPreviousTasks = () => {
    if (!activeTask) {
      return null;
    }

    let activeTaskFound = false;

    return props.examTasks.map((examTask: ExamTask) => {
      if (!activeTaskFound && examTask.taskNumber !== activeTask.taskNumber) {
        return (
          <div key={examTask.taskId}>
            <TaskScore
              mode={props.mode}
              key={examTask.taskNumber}
              examTask={examTask}
              handleSelectTask={handleSelectTask}
              handleSelectCriterion={handleSelectCriterion}
              examTaskGroupSolutionScoreState={props.examTaskGroupSolutionScoreState}
              processTypeId={props.processTypeId}
              taskToleranceRange={taskToleranceRange ?? null}
              examinerFunction={props.examinerFunction}
            />
          </div>
        );
      }
      if (examTask.taskNumber === activeTask.taskNumber) {
        activeTaskFound = true;
      }
    });
  };

  const getNextTasks = () => {
    if (!activeTask) {
      return null;
    }

    let activeTaskFound = false;

    return props.examTasks.map((examTask: ExamTask) => {
      if (activeTaskFound && examTask.taskNumber !== activeTask.taskNumber) {
        return (
          <div key={examTask.taskId} className={classes.nextTaskScore}>
            <TaskScore
              mode={props.mode}
              key={examTask.taskNumber}
              examTask={examTask}
              handleSelectTask={handleSelectTask}
              handleSelectCriterion={handleSelectCriterion}
              examTaskGroupSolutionScoreState={props.examTaskGroupSolutionScoreState}
              processTypeId={props.processTypeId}
              taskToleranceRange={taskToleranceRange ?? null}
              examinerFunction={props.examinerFunction}
            />
          </div>
        );
      }

      if (examTask.taskNumber === activeTask.taskNumber) {
        activeTaskFound = true;
      }
    });
  };

  return (
    <div className={classes.container}>
      {props.examTasks && (
        <div className={classes.previousTaskContainer}>
          {getPreviousTasks()}

          {props.examTasks.map(
            (examTask: ExamTask) =>
              activeTask &&
              examTask.taskNumber === activeTask.taskNumber &&
              (props.activeCriterionIndex > -1 || activeCriterion) && (
                <div key={examTask.taskId}>
                  <TaskScore
                    mode={props.mode}
                    key={examTask.taskNumber}
                    examTask={examTask}
                    handleSelectTask={handleSelectTask}
                    handleSelectCriterion={handleSelectCriterion}
                    examTaskGroupSolutionScoreState={props.examTaskGroupSolutionScoreState}
                    processTypeId={props.processTypeId}
                    taskToleranceRange={taskToleranceRange ?? null}
                    isPreviousCriteria={true}
                    examinerFunction={props.examinerFunction}
                  />
                </div>
              ),
          )}
        </div>
      )}
      {activeTask && props.examTasks.length > 0 && (
        <div className={classes.activeTaskContainer}>
          <ActiveTaskScore
            mode={props.mode}
            examTasksLength={props.examTasks.length}
            key={activeTask?.taskNumber}
            viewType={props.viewType}
            onTaskChange={props.onTaskChange}
            onCriterionChange={props.onCriterionChange}
            activeTaskIndex={props.activeTaskIndex}
            examTaskGroupSolutionsNavigation={props.examTaskGroupSolutionsNavigation}
            examinerFunction={props.examinerFunction}
            examTaskGroupSolutionScoreState={props.examTaskGroupSolutionScoreState}
            examinerId={props.examinerId}
            handleSelectTask={handleSelectTask}
            handleSelectCriterion={handleSelectCriterion}
            canEditScore={props.canEditScore}
            processTypeId={props.processTypeId}
            taskToleranceRange={taskToleranceRange ?? null}
            orgzId={props.orgzId}
            anyAnnotationForGradingProvided={props.anyAnnotationForGradingProvided}
            isFullscreen={props.isFullscreen}
            isAnyModalOpened={props.isAnyModalOpened}
            keyboardShortcutsDisabled={props.keyboardShortcutsDisabled}
            isSkipLink={props.isSkipLink}
          />
          {getNextTasks()}
        </div>
      )}
      {props.examTasks.length === 0 && (
        <EmptyTasksList viewType={props.viewType} activeTaskNo={props.activeTaskNo} onTaskChange={props.onTaskChange} />
      )}
    </div>
  );
};
